import { TokenKey, UserInfoKey } from "@/constants";
import { CustomApiError } from "@/utils/error";
import { subscribeNotification } from "@/utils/notification";
import { getLanguage } from "@/i18n";
import type { LanguageType } from "@/i18n/type";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { App, ConfigProvider } from "antd";
import zhCN from "antd/es/locale/zh_CN";
import zhHK from "antd/es/locale/zh_HK";
import enUS from "antd/es/locale/en_US";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { ModalProvider } from "@/hooks/context/ModalContext";
import { FilePreviewProvider } from "@/hooks/context/FilePreviewContext";
import { ModalHost } from "../ModalHost";

// 获取 Ant Design locale
const getAntdLocale = (language: string) => {
  switch (language) {
    case "zh-HK":
      return zhHK;
    case "en-US":
      return enUS; // Ant Design 默认英文
    case "zh-CN":
    default:
      return zhCN;
  }
};

function handleUnauthorized() {
  const currentPath = window.location.pathname;
  // 避免在登录页内无限跳转
  if (currentPath !== "#/login") {
    localStorage.removeItem(TokenKey);
    localStorage.removeItem(UserInfoKey);
    window.location.hash = "/login";
  }
}

const queryCache = new QueryCache({
  onError: (error: unknown) => {
    console.log("🚀 ~ error:", error);
    if (error instanceof CustomApiError) {
      const { code } = error.response.data;
      if (code === 401) {
        handleUnauthorized();
      }
    }

    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 401) {
        handleUnauthorized();
      }
      return;
    }
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      networkMode: "always",
    },
    mutations: {
      networkMode: "always",
    },
  },
  queryCache: queryCache,
});

const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<LanguageType>(() => getLanguage());

  useEffect(() => {
    // 监听语言变化
    const handleLanguageChange = () => {
      setLanguage(getLanguage());
    };

    // 订阅 i18n 语言变化事件
    window.addEventListener("languagechange", handleLanguageChange);

    return () => {
      window.removeEventListener("languagechange", handleLanguageChange);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        locale={getAntdLocale(language)}
        theme={{
          components: {
            Calendar: {
              miniContentHeight: 250,
            },
            Tabs: {
              itemSelectedColor: "#165DFF",
              inkBarColor: "#165DFF",
            },
            Table: {
              cellPaddingBlockSM: 11,
            },
            Timeline: {
              dotBg: "transparent",
            },
          },
        }}
      >
        <App>
          <ModalProvider>
            <NotificationListener>
              <FilePreviewProvider>
                {children}
                <ModalHost />
              </FilePreviewProvider>
            </NotificationListener>
          </ModalProvider>
        </App>
      </ConfigProvider>
    </QueryClientProvider>
  );
};

// ==================== 通知监听组件 ====================
/**
 * 订阅全局通知事件并显示提示信息
 */
const NotificationListener: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { message } = App.useApp();

  useEffect(() => {
    // 订阅通知事件
    const unsubscribe = subscribeNotification(({ type, message: msg }) => {
      message[type](msg);
    });

    return unsubscribe;
  }, [message]);

  return <>{children}</>;
};

export default AppProvider;
